/**
 * Gated HITL bridges for markers (point structures), like the structure
 * list: `create_marker` = "+ New → Point" (placed at the crosshair, or at
 * an explicit position) and `set_marker_position` = the marker position
 * fields (X/Y/Z in cm). Positions are DICOM patient coordinates; the model
 * sends cm, converted to mm at the shared choke-point. A position outside
 * the scan is refused (a marker off the image is almost certainly a unit
 * or sign mistake). Refused for an approved set / marker. Apply re-checks
 * and confirms by reading the marker back. Pure/React-free.
 */
import { validateStructureName } from '~/utils/validation';
import {
    asRecord,
    cmToMmChokepoint,
    failedPlan,
    mutatingPreamble,
    type MutatingPlan,
    type MutatingToolCall,
} from '../core';
import {
    gatedChange,
    settle,
    type ChangeProposal,
} from '../core/changeProposal';
import type { StructureChangeOutcome } from '../../contract';
import {
    approvedReason,
    liveRoi,
    resolveAnyRoi,
    type LiveDeps,
    type PointMm,
} from '../structureManagement/liveState';

export type MarkerProposal = ChangeProposal<StructureChangeOutcome>;

export interface MarkerDeps extends LiveDeps {
    /** The crosshair position (mm); undefined without an image. */
    crosshairMm(): PointMm | undefined;
    /** Is this position (mm) inside the loaded image? */
    insideImage(pointMm: PointMm): boolean;
    /** "+ New → Point" at this position (mm). */
    createMarker(pointMm: PointMm): Promise<void>;
    rename(roiNumber: number, name: string): Promise<void>;
    /** The marker position fields' change step (mm). */
    setPosition(roiNumber: number, pointMm: PointMm): Promise<void>;
}

type Plan = MutatingPlan<MarkerProposal>;

/** Tolerance for confirming a written position (mm). */
const POSITION_TOLERANCE_MM = 0.001;

/** X/Y/Z in cm as the fields show them (2 dp). */
export function formatPositionCm(p: PointMm): string {
    return (
        `X ${(p[0] / 10).toFixed(2)}, Y ${(p[1] / 10).toFixed(2)}, ` +
        `Z ${(p[2] / 10).toFixed(2)} cm`
    );
}

/** Read {x,y,z} cm from the model's input; undefined if absent/invalid. */
function readPositionCm(v: unknown): PointMm | 'invalid' | undefined {
    if (v === undefined) {
        return undefined;
    }
    const o = asRecord(v);
    const xs = [o?.x, o?.y, o?.z];
    if (!xs.every((n) => typeof n === 'number' && Number.isFinite(n))) {
        return 'invalid';
    }
    const [x, y, z] = xs as number[];
    return [cmToMmChokepoint(x), cmToMmChokepoint(y), cmToMmChokepoint(z)];
}

const samePoint = (a: PointMm | undefined, b: PointMm) =>
    !!a && a.every((v, i) => Math.abs(v - b[i]) <= POSITION_TOLERANCE_MM);

const OUTSIDE =
    'that position is outside the scan; check the X/Y/Z values (cm, as ' +
    'the viewer shows them)';

export async function planCreateMarker(
    call: MutatingToolCall,
    deps: MarkerDeps
): Promise<Plan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const o = asRecord(call.input) ?? {};
    const set = deps.liveSet();
    if (!set) {
        return failedPlan(call.toolCallId, 'no structure set is loaded');
    }
    if (set.approvalStatus === 'APPROVED') {
        return failedPlan(
            call.toolCallId,
            'the structure set is approved; markers cannot be added'
        );
    }
    const given = readPositionCm(o.positionCm);
    if (given === 'invalid') {
        return failedPlan(call.toolCallId, 'positionCm needs numeric x, y, z');
    }
    const pointMm = given ?? deps.crosshairMm();
    if (!pointMm) {
        return failedPlan(call.toolCallId, 'no image is loaded');
    }
    if (!deps.insideImage(pointMm)) {
        return failedPlan(call.toolCallId, OUTSIDE);
    }
    const name = typeof o.name === 'string' ? o.name.trim() : undefined;
    const nameProblem = () => {
        if (name === undefined) {
            return undefined;
        }
        const [ok, why] = validateStructureName(
            name,
            (deps.liveSet()?.rois ?? []).map((r) => r.name)
        );
        return ok ? undefined : (why ?? 'invalid name');
    };
    const problem = nameProblem();
    if (problem) {
        return failedPlan(call.toolCallId, problem);
    }
    const where = formatPositionCm(pointMm);
    const before = new Set(set.rois.map((r) => r.roiNumber));
    return {
        kind: 'proposal',
        toolCallId: call.toolCallId,
        proposal: gatedChange<StructureChangeOutcome>({
            toolCallId: call.toolCallId,
            title: 'New marker',
            summary:
                `add marker${name ? ` “${name}”` : ''} at ${where}` +
                (given ? '' : ' (the crosshair)'),
            undoable: false,
            currentContextId: deps.currentContextId,
            recheck: () =>
                deps.liveSet()?.approvalStatus === 'APPROVED'
                    ? 'the structure set is approved'
                    : nameProblem(),
            run: async () => {
                await deps.createMarker(pointMm);
                await settle();
                const created = (deps.liveSet()?.rois ?? []).find(
                    (r) => !before.has(r.roiNumber) && r.isPointRoi
                );
                if (!created) {
                    return {
                        status: 'failed',
                        reason: 'the viewer did not create the marker',
                    };
                }
                let finalName = created.name;
                let note = '';
                if (name && name !== created.name) {
                    await deps.rename(created.roiNumber, name);
                    await settle();
                    finalName =
                        liveRoi(deps, created.roiNumber)?.name ?? created.name;
                    if (finalName !== name) {
                        note = ` (it could not be named '${name}')`;
                    }
                }
                return {
                    status: 'applied',
                    roiNumber: created.roiNumber,
                    roi: finalName,
                    summary: `added marker '${finalName}' at ${where}${note}`,
                };
            },
        }),
    };
}

export async function planSetMarkerPosition(
    call: MutatingToolCall,
    deps: MarkerDeps
): Promise<Plan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const o = asRecord(call.input);
    const r = resolveAnyRoi(call.toolCallId, o?.roi, deps);
    if (r.kind !== 'ok') {
        return r;
    }
    const { roiNumber, name, isPointRoi, pointMm: from } = r.roi;
    if (!isPointRoi) {
        return failedPlan(
            call.toolCallId,
            `'${name}' is not a marker; only markers have a position`
        );
    }
    if (r.roi.isApproved) {
        return failedPlan(call.toolCallId, approvedReason(name));
    }
    const to = readPositionCm(o?.positionCm);
    if (to === undefined || to === 'invalid') {
        return failedPlan(call.toolCallId, 'positionCm needs numeric x, y, z');
    }
    if (!deps.insideImage(to)) {
        return failedPlan(call.toolCallId, OUTSIDE);
    }
    if (samePoint(from, to)) {
        return failedPlan(call.toolCallId, `'${name}' is already there`);
    }
    return {
        kind: 'proposal',
        toolCallId: call.toolCallId,
        proposal: gatedChange<StructureChangeOutcome>({
            toolCallId: call.toolCallId,
            title: 'Move marker',
            summary: `move “${name}” to ${formatPositionCm(to)}`,
            details: from ? [`Now at ${formatPositionCm(from)}`] : [],
            undoable: false,
            currentContextId: deps.currentContextId,
            recheck: () =>
                liveRoi(deps, roiNumber)?.isApproved
                    ? approvedReason(name)
                    : undefined,
            run: async () => {
                await deps.setPosition(roiNumber, to);
                await settle();
                if (!samePoint(liveRoi(deps, roiNumber)?.pointMm, to)) {
                    return {
                        status: 'failed',
                        reason: 'the viewer did not move the marker',
                    };
                }
                return {
                    status: 'applied',
                    roiNumber,
                    roi: name,
                    summary: `moved '${name}' to ${formatPositionCm(to)}`,
                };
            },
        }),
    };
}
